import { createTheme, ThemeOptions, Theme, alpha } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { getActivePalette } from './config/colorPalette';

// ============================================================================
// CUSTOMIZE YOUR COLOR PALETTE HERE
// ============================================================================
// To customize colors, modify the values in src/config/colorPalette.ts
// - primary: Main brand color (buttons, links, focus states)
// - secondary: Accent color (highlights, secondary actions)
// - success: Positive actions and health indicators
// - petAccents: Special pet-themed colors for fun elements
// - health: Health status indicators
// ============================================================================

// Get the active color palette from configuration
const petHealthColors = getActivePalette();

// Modern Healthcare Typography System
// Phase 3 (2026-04-28): Reordered to put -apple-system first so iOS / macOS
// natively resolve to SF Pro Text / Display, giving the app the same default
// typeface as Apple Health and the system. Inter remains as the web/Android
// fallback for visual consistency with our marketing site and brand.
const typography = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Inter", "Segoe UI", Roboto, sans-serif',
  
  // Display Headings - For hero sections and major titles
  h1: {
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: 'inherit',
  },
  h2: {
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    color: 'inherit',
  },
  
  // Section Headings - For dashboard sections and cards
  h3: {
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'inherit',
  },
  h4: {
    fontSize: 'clamp(1.25rem, 2.5vw, 1.875rem)',
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'inherit',
  },
  
  // Card Headings - For document titles and alerts
  h5: {
    fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
    fontWeight: 600,
    lineHeight: 1.5,
    color: 'inherit',
  },
  h6: {
    fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
    fontWeight: 600,
    lineHeight: 1.5,
    color: 'inherit',
  },
  
  // Body Text - Optimized for medical data readability
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
    color: 'inherit',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    color: 'inherit',
  },
  
  // Medical Data - For lab results and health metrics
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
    color: 'inherit',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.01em',
    color: 'inherit',
  },
  
  // Action Text - For buttons and interactive elements
  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
    textTransform: 'none' as const,
    color: 'inherit',
  },
  
  // Status Text - For confidence scores and alerts
  caption: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.02em',
    color: 'inherit',
  },
  
  // Overline - For categories and labels
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: 'inherit',
  },
};

// Pet-themed gradient patterns using the active color palette (reserved for future use)
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
const _petGradients = {
  primary: `linear-gradient(135deg, ${petHealthColors.primary[500]} 0%, ${petHealthColors.primary[600]} 100%)`,
  secondary: `linear-gradient(135deg, ${petHealthColors.secondary[500]} 0%, ${petHealthColors.secondary[600]} 100%)`,
  success: `linear-gradient(135deg, ${petHealthColors.success[500]} 0%, ${petHealthColors.success[600]} 100%)`,
  warm: `linear-gradient(135deg, ${petHealthColors.petAccents.warm} 0%, ${petHealthColors.petAccents.warm}dd 100%)`,
  playful: `linear-gradient(135deg, ${petHealthColors.petAccents.playful} 0%, ${petHealthColors.petAccents.playful}dd 100%)`,
  gentle: `linear-gradient(135deg, ${petHealthColors.petAccents.gentle} 0%, ${petHealthColors.petAccents.gentle}dd 100%)`,
  health: `linear-gradient(135deg, ${petHealthColors.health.excellent} 0%, ${petHealthColors.health.good} 100%)`,
  // Fun pet-themed gradients
  sunrise: `linear-gradient(135deg, ${petHealthColors.petAccents.warm} 0%, ${petHealthColors.petAccents.playful} 50%, ${petHealthColors.petAccents.gentle} 100%)`,
  ocean: `linear-gradient(135deg, ${petHealthColors.primary[500]} 0%, ${petHealthColors.secondary[500]} 100%)`,
  forest: `linear-gradient(135deg, ${petHealthColors.success[500]} 0%, ${petHealthColors.success[400]} 100%)`,
};

// Enhanced shadows with more depth
const modernShadows = [
  'none',
  '0px 1px 3px rgba(0, 0, 0, 0.06), 0px 1px 2px rgba(0, 0, 0, 0.04)',
  '0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)',
  '0px 10px 15px rgba(0, 0, 0, 0.08), 0px 4px 6px rgba(0, 0, 0, 0.07)',
  '0px 20px 25px rgba(0, 0, 0, 0.10), 0px 10px 10px rgba(0, 0, 0, 0.04)',
  '0px 25px 50px rgba(0, 0, 0, 0.12), 0px 12px 20px rgba(0, 0, 0, 0.08)',
  // Pet-themed special shadows
  'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 16px rgba(0,0,0,0.1)',
  '0 20px 40px rgba(0,145,255,0.15), 0 8px 16px rgba(0,145,255,0.1)',
  '0 20px 40px rgba(255,145,0,0.15), 0 8px 16px rgba(255,145,0,0.1)',
  '0 20px 40px rgba(26,188,156,0.15), 0 8px 16px rgba(26,188,156,0.1)',
  // Continue with remaining shadow levels...
  ...Array.from({ length: 15 }, (_, i) => {
    const intensity = (i + 10) * 2;
    return `0px ${intensity}px ${intensity * 2}px rgba(0, 0, 0, ${0.1 + i * 0.01})`;
  })
];

// Modern border radius scale
const borderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  '2xl': 32,
  '3xl': 48,
  full: 9999,
};

// Common theme configuration with modern styling
const commonTheme: ThemeOptions = {
  typography,
  shadows: modernShadows as ThemeOptions['shadows'],
  shape: {
    borderRadius: borderRadius.md,
  },
  spacing: 8, // 8px base spacing
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': {
          fontFamily: 'Inter',
          fontStyle: 'normal',
          fontDisplay: 'swap',
        },
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
          width: '100%',
          margin: 0,
          padding: 0,
        },
        '#root': {
          height: '100%',
          width: '100%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          padding: '12px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: 1.5,
          letterSpacing: '0.02em',
          textTransform: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 'none',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineOffset: '2px',
          },
        },
        contained: {
          '&:hover': {
            backgroundColor: (theme: Theme) => theme.palette.secondary.main,
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.20)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: (theme: Theme) => alpha(theme.palette.secondary.main, 0.08),
            borderColor: (theme: Theme) => theme.palette.secondary.main,
            color: (theme: Theme) => theme.palette.secondary.main,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: (theme: Theme) => alpha(theme.palette.secondary.main, 0.08),
            color: (theme: Theme) => theme.palette.secondary.main,
          },
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.8125rem',
          borderRadius: borderRadius.sm,
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1rem',
          borderRadius: borderRadius.xl,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          border: '1px solid',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xl,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid',
        },
        elevation0: {
          border: '1px solid',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.lg,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },
            },
            '@media (hover: hover)': {
              '&.Mui-focused': {
                transform: 'scale(1.005)',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          fontWeight: 500,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'scale(1.05)',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'scale(1.1)',
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          margin: '2px 8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '@media (hover: hover)': {
            '&:hover': {
              transform: 'translateX(4px)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius['2xl'],
          border: '1px solid',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
  },
};

// Solid background — same as drawer/nav so body and nav read as one surface.
const getBackgroundPattern = (mode: PaletteMode) => {
  return mode === 'dark' ? '#141614' : '#FFFFFF';
};

const getDesignTokens = (mode: PaletteMode): ThemeOptions => {
  const isLight = mode === 'light';
  
  return {
    ...commonTheme,
    palette: {
      mode,
      primary: {
        ...petHealthColors.primary,
        main: petHealthColors.primary[500],
      },
      secondary: {
        ...petHealthColors.secondary,
        main: petHealthColors.secondary[500],
      },
      success: {
        ...petHealthColors.success,
        main: petHealthColors.success[500],
      },
      background: {
        default: isLight ? '#FFFFFF' : '#141614',
        paper: isLight ? '#FFFFFF' : '#1F211F',
      },
      text: {
        primary: isLight ? '#1A1A1A' : '#F5F5F0',
        secondary: isLight ? '#6B6B6B' : '#B3B3B0',
        disabled: isLight ? '#999999' : '#7F7F7A',
      },
      divider: isLight ? '#EBEBEB' : '#2B2D2B',
      action: {
        hover: isLight ? alpha('#1A1A1A', 0.04) : alpha('#F5F5F0', 0.08),
        selected: isLight ? alpha('#1A1A1A', 0.08) : alpha('#F5F5F0', 0.12),
        disabled: isLight ? alpha('#1A1A1A', 0.26) : alpha('#F5F5F0', 0.26),
        disabledBackground: isLight ? alpha('#1A1A1A', 0.12) : alpha('#F5F5F0', 0.12),
      },
      error: {
        50: '#FDF2F2',
        100: '#FEE2E2',
        200: '#FECACA',
        300: '#FCA5A5',
        400: '#F87171',
        500: '#E8B4B8', // Soft rose from custom palette
        600: '#DC2626',
        700: '#B91C1C',
        800: '#991B1B',
        900: '#7F1D1D',
        main: '#E8B4B8', // Soft rose from custom palette
      },
      warning: {
        50: '#FFFBEB',
        100: '#FEF3C7',
        200: '#FDE68A',
        300: '#FCD34D',
        400: '#FBBF24',
        500: '#F9E79F', // Soft yellow from custom palette
        600: '#D97706',
        700: '#B45309',
        800: '#92400E',
        900: '#78350F',
        main: '#F9E79F', // Soft yellow from custom palette
      },
      info: {
        50: '#F0F9FF',
        100: '#E0F2FE',
        200: '#BAE6FD',
        300: '#7DD3FC',
        400: '#38BDF8',
        500: '#A8D5BA', // Soft mint from custom palette
        600: '#0284C7',
        700: '#0369A1',
        800: '#075985',
        900: '#0C4A6E',
        main: '#A8D5BA', // Soft mint from custom palette
      },
      // Custom fresh mint color for specific use cases
      freshMint: {
        50: '#F0FDFA',
        100: '#CCFBF1',
        200: '#99F6E4',
        300: '#5EEAD4',
        400: '#2DD4BF',
        500: '#A8D8EA', // Sky blue from custom palette
        600: '#0D9488',
        700: '#0F766E',
        800: '#115E59',
        900: '#134E4A',
                 main: '#A8D8EA', // Sky blue from custom palette
      },
    },
    components: {
      ...(commonTheme.components || {}),
      MuiCssBaseline: {
        styleOverrides: {
          ...(commonTheme.components?.MuiCssBaseline?.styleOverrides as object || {}),
          body: {
            background: getBackgroundPattern(mode),
            minHeight: '100dvh',
            backgroundAttachment: 'fixed',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            ...(commonTheme.components?.MuiCard?.styleOverrides?.root as object || {}),
            backgroundColor: isLight ? alpha('#FFFFFF', 0.8) : alpha('#1F211F', 0.8),
            borderColor: isLight ? '#EBEBEB' : '#2B2D2B',
            boxShadow: isLight
              ? '0 8px 32px rgba(0, 0, 0, 0.08)'
              : '0 8px 32px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            ...(commonTheme.components?.MuiPaper?.styleOverrides?.root as object || {}),
            backgroundColor: isLight ? alpha('#FFFFFF', 0.8) : alpha('#1F211F', 0.8),
            borderColor: isLight ? '#EBEBEB' : '#2B2D2B',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          ...(commonTheme.components?.MuiButton?.styleOverrides as object || {}),
          root: {
            ...(commonTheme.components?.MuiButton?.styleOverrides?.root as object || {}),
            '&:focus-visible': {
              outlineColor: petHealthColors.primary[500],
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            ...(commonTheme.components?.MuiTextField?.styleOverrides?.root as object || {}),
            '& .MuiOutlinedInput-root': {
              backgroundColor: isLight ? alpha('#FFFFFF', 0.5) : alpha('#1F211F', 0.5),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isLight ? '#EBEBEB' : '#2B2D2B',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: petHealthColors.primary[isLight ? 400 : 600],
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: petHealthColors.primary[500],
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...(commonTheme.components?.MuiDialog?.styleOverrides?.paper as object || {}),
            backgroundColor: isLight ? alpha('#FFFFFF', 0.95) : alpha('#1F211F', 0.95),
            backdropFilter: 'blur(40px)',
            borderColor: isLight ? '#EBEBEB' : '#2B2D2B',
            boxShadow: isLight 
              ? '0 20px 64px rgba(0, 0, 0, 0.15)' 
              : '0 20px 64px rgba(0, 0, 0, 0.5)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#FFFFFF !important' : `${alpha('#1F211F', 0.95)} !important`, // Force exact match to drawer in dark mode
            backdropFilter: 'blur(20px)',
            borderBottom: isLight ? `1px solid #EBEBEB` : `1px solid #2B2D2B`,
            boxShadow: 'none',
            color: isLight ? '#1A1A1A !important' : '#F5F5F0', // Force text color in dark mode
            border: 'none',
            '&.MuiAppBar-root': {
              backgroundColor: isLight ? '#FFFFFF !important' : `${alpha('#1F211F', 0.95)} !important`, // Force exact match to drawer
              color: isLight ? '#1A1A1A !important' : '#F5F5F0',
            }
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? alpha('#FFFFFF', 0.95) : alpha('#1F211F', 0.95), // Keep original drawer theme
            backdropFilter: 'blur(20px)',
            borderRight: isLight ? `1px solid #EBEBEB` : `1px solid #2B2D2B`, // Keep original drawer border
            color: isLight ? '#1A1A1A' : '#F5F5F0', // Keep original drawer text
            borderRadius: 0, // Make all drawers rectangular with no rounded corners
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: isLight ? '#1A1A1A' : '#F5F5F0', // Use theme text.primary by default in dark mode
          },
        },
      },
    },
  };
};

// Utility function to access pet-themed colors
export const getPetThemeColors = () => petHealthColors;

// Utility function to get specific pet colors
export const getPetColor = (colorType: keyof typeof petHealthColors.petAccents) => {
  return petHealthColors.petAccents[colorType];
};

// Utility function to get health status colors
export const getHealthStatusColor = (status: keyof typeof petHealthColors.health) => {
  return petHealthColors.health[status];
};

// Export themes
export const lightTheme = createTheme(getDesignTokens('light'));
export const darkTheme = createTheme(getDesignTokens('dark'));

export default lightTheme; 